import React, { useEffect, useRef, useState } from 'react';
import {
  User, Mail, Calendar, Hash, Sparkles, Cake, Globe2, Clock4,
  KeyRound, Activity, TrendingUp, Award, ShieldCheck, AtSign,
  Pencil, Check, X, Loader2, Camera, Heart, Zap, Layers,
  ChevronRight, ArrowUpRight, Star,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader, Stat } from '../components/Tabs';
import { Button } from '../components/Button';
import { Badge, Dot } from '../components/Badge';
import { Input } from '../components/Input';
import { supabase } from '../supabaseClient';

/**
 * ProfilePage — customer account profile, redesigned.
 *
 * Layout (top to bottom):
 *   1. Cosy hero card with avatar + greeting + quick stats
 *   2. Identity card (editable display name, email, account id, member-since)
 *   3. Plan + capacity card (current plan, credits, concurrency, refills)
 *   4. Activity ribbon (this-month usage, all-time keys, all-time requests)
 *   5. Tiny footer card with a tiny reassurance line
 */
export default function ProfilePage({ user, billing, usageThisMonth = null, apiKeyCount = 0 }) {
  const meta = user?.user_metadata || {};

  // Display name derivation
  function pickDisplayName() {
    if (meta.display_name?.trim()) return meta.display_name.trim();
    if (meta.full_name?.trim()) return meta.full_name.trim();
    const fn = (meta.first_name || '').trim();
    const ln = (meta.last_name || '').trim();
    if (fn || ln) return `${fn} ${ln}`.trim();
    if (user?.email?.includes('@')) {
      return user.email.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return user?.email || 'Friend';
  }

  const initial = pickDisplayName().slice(0, 1).toUpperCase();
  const email = user?.email || '';
  const customerId = user?.id || billing?.customer_id || '—';
  const memberSince = user?.created_at || billing?.subscription_start || null;
  const planName = billing?.plan_display_name || billing?.plan_code || 'Free';
  const refill = billing?.initial_credits ?? 0;
  const balance = billing?.credit_balance ?? 0;
  const conc = billing?.rate_limit_per_minute ?? 1;
  const refillPct = refill ? Math.min(100, Math.round((balance / refill) * 100)) : 0;

  // Friendly greeting that varies by time-of-day (Kurdish + English mix would be
  // cool but we keep it neutral + warm so it works in every locale).
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return 'Still up?';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  // Days as a member
  const daysAsMember = memberSince
    ? Math.max(1, Math.floor((Date.now() - new Date(memberSince).getTime()) / 86400000))
    : 0;

  return (
    <div className="space-y-6 max-w-5xl">
      <SectionHeader
        title="My profile"
        description="Your home on Datames — make it cosy."
      />

      {/* ============================================================
          1. HERO CARD — warm, gradient, signature
         ============================================================ */}
      <HeroCard
        greeting={greeting}
        displayName={pickDisplayName()}
        email={email}
        initial={initial}
        planName={planName}
        refillPct={refillPct}
        balance={balance}
        refill={refill}
        daysAsMember={daysAsMember}
      />

      {/* ============================================================
          2. IDENTITY — editable display name + account info
         ============================================================ */}
      <IdentityCard
        user={user}
        displayName={pickDisplayName()}
        email={email}
        customerId={customerId}
        memberSince={memberSince}
      />

      {/* ============================================================
          3. PLAN & CAPACITY
         ============================================================ */}
      <CapacityCard
        planName={planName}
        balance={balance}
        refill={refill}
        refillPct={refillPct}
        concurrency={conc}
        planCode={billing?.plan_code || 'free'}
        billingInterval={billing?.billing_interval || 'monthly'}
      />

      {/* ============================================================
          4. ACTIVITY RIBBON
         ============================================================ */}
      <ActivityCard
        usageThisMonth={usageThisMonth}
        apiKeyCount={apiKeyCount}
        memberSince={memberSince}
      />
    </div>
  );
}

/* ------------------------------------------------------------------
   HERO CARD
   ------------------------------------------------------------------ */
function HeroCard({ greeting, displayName, email, initial, planName, refillPct, balance, refill, daysAsMember }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
      {/* Decorative gradient banner */}
      <div
        aria-hidden="true"
        className="h-28 w-full"
        style={{
          background:
            'linear-gradient(120deg, hsl(15 100% 60%) 0%, hsl(28 100% 65%) 35%, hsl(50 100% 75%) 75%, hsl(15 100% 90%) 100%)',
        }}
      >
        {/* Decorative blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-white/20 blur-2xl" />
          <div className="absolute top-8 right-32 h-20 w-20 rounded-full bg-white/30 blur-xl" />
          <div className="absolute top-16 left-1/3 h-16 w-16 rounded-full bg-yellow-200/40 blur-lg" />
        </div>
      </div>

      <div className="px-6 pb-6 -mt-12 relative">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          {/* Avatar with animated ring */}
          <div className="relative shrink-0">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-400 via-pink-400 to-yellow-300 blur-md opacity-60 animate-pulse" />
            <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 border-4 border-surface shadow-lg flex items-center justify-center text-white text-3xl font-semibold">
              {initial}
            </div>
            <span className="absolute bottom-1 right-1 h-5 w-5 rounded-full bg-success border-2 border-surface" aria-label="Online" />
          </div>

          <div className="flex-1 min-w-0 md:pb-1">
            <div className="text-2xs uppercase tracking-wider text-fg-subtle font-medium">
              {greeting} ✦
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-fg mt-0.5 truncate">
              {displayName}
            </h1>
            <div className="flex items-center gap-1.5 text-sm text-fg-muted mt-0.5 min-w-0">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{email}</span>
            </div>
          </div>

          {/* Plan pill */}
          <div className="flex items-center gap-2 md:pb-1">
            <Badge variant="brand" className="px-2.5 py-1 text-sm gap-1">
              <Sparkles className="h-3.5 w-3.5" />
              {planName}
            </Badge>
            {daysAsMember > 0 && (
              <Badge variant="default" className="px-2.5 py-1 text-xs gap-1">
                <Cake className="h-3 w-3" />
                {daysAsMember} {daysAsMember === 1 ? 'day' : 'days'} with us
              </Badge>
            )}
          </div>
        </div>

        {/* Mini balance meter */}
        {refill > 0 && (
          <div className="mt-5 rounded-xl bg-surface-2/60 border border-border p-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-fg-muted flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-warning" />
                This month's credits
              </span>
              <span className="font-mono font-medium text-fg">
                {balance.toLocaleString()} <span className="text-fg-subtle">/ {refill.toLocaleString()}</span>
              </span>
            </div>
            <div className="relative h-2 rounded-full bg-surface overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                style={{
                  width: `${refillPct}%`,
                  background: refillPct > 80
                    ? 'linear-gradient(90deg, hsl(83 40% 49%) 0%, hsl(48 100% 50%) 100%)'
                    : refillPct < 20
                    ? 'linear-gradient(90deg, hsl(351 76% 50%) 0%, hsl(15 100% 50%) 100%)'
                    : 'linear-gradient(90deg, hsl(15 100% 50%) 0%, hsl(35 100% 60%) 100%)',
                }}
              />
            </div>
            <div className="mt-1.5 text-2xs text-fg-subtle">
              {refillPct}% remaining — refreshing on the 1st of next month
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   IDENTITY CARD — editable display name + account info
   ------------------------------------------------------------------ */
function IdentityCard({ user, displayName, email, customerId, memberSince }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayName);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(displayName); }, [displayName]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  async function save() {
    if (!draft.trim()) {
      setMsg({ type: 'error', text: 'Display name can\'t be empty' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: draft.trim() },
      });
      if (error) throw error;
      setMsg({ type: 'success', text: 'Display name updated.' });
      setEditing(false);
      // Soft refresh of user metadata in the parent — but we can't easily,
      // so we rely on the parent re-rendering via auth state change.
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setDraft(displayName);
    setEditing(false);
    setMsg(null);
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-brand-softest border border-brand-soft flex items-center justify-center">
            <User className="h-4 w-4 text-brand-strong" />
          </div>
          <div>
            <Card.Title>Identity</Card.Title>
            <Card.Subtitle>How you appear across Datames.</Card.Subtitle>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        {/* Editable display name */}
        <div className="rounded-xl bg-surface-2/50 border border-border p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-2xs uppercase tracking-wider text-fg-subtle font-medium flex items-center gap-1.5">
                <AtSign className="h-3 w-3" /> Display name
              </div>
              {editing ? (
                <div className="mt-1.5">
                  <Input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') save();
                      if (e.key === 'Escape') cancel();
                    }}
                    className="text-base"
                  />
                  <div className="mt-1 text-2xs text-fg-subtle">
                    Press <kbd className="px-1 py-0.5 rounded bg-surface border border-border font-mono text-2xs">Enter</kbd> to save · <kbd className="px-1 py-0.5 rounded bg-surface border border-border font-mono text-2xs">Esc</kbd> to cancel
                  </div>
                </div>
              ) : (
                <div className="text-base text-fg mt-1 truncate">{displayName}</div>
              )}
            </div>
            {editing ? (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-success text-success-fg text-xs font-medium hover:opacity-90 disabled:opacity-50 transition"
                  aria-label="Save"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-surface border border-border text-fg-muted hover:text-fg text-xs font-medium transition"
                  aria-label="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-surface border border-border text-fg-muted hover:text-fg text-xs font-medium transition shrink-0"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Read-only fields */}
        <div className="grid sm:grid-cols-2 gap-3">
          <InfoTile icon={Mail} label="Email" value={email || '—'} mono />
          <InfoTile icon={Hash} label="Account ID" value={customerId} mono copyable />
          <InfoTile
            icon={Calendar}
            label="Member since"
            value={memberSince ? new Date(memberSince).toLocaleDateString(undefined, {
              year: 'numeric', month: 'long', day: 'numeric',
            }) : '—'}
          />
          <InfoTile
            icon={Clock4}
            label="Last sign-in"
            value={user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : '—'}
          />
        </div>

        {msg && (
          <div className={`px-3 py-2 rounded-md text-xs border ${
            msg.type === 'success'
              ? 'bg-success/10 border-success/30 text-success'
              : 'bg-destructive/10 border-destructive/30 text-destructive'
          }`}>
            {msg.text}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/* ------------------------------------------------------------------
   CAPACITY CARD — plan + usage stats
   ------------------------------------------------------------------ */
function CapacityCard({ planName, balance, refill, refillPct, concurrency, planCode, billingInterval }) {
  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-brand-softest border border-brand-soft flex items-center justify-center">
            <Layers className="h-4 w-4 text-brand-strong" />
          </div>
          <div className="flex-1">
            <Card.Title>Plan & capacity</Card.Title>
            <Card.Subtitle>What your subscription unlocks.</Card.Subtitle>
          </div>
          <a
            href="/billing"
            className="inline-flex items-center gap-1 text-2xs font-medium text-brand hover:underline"
          >
            Manage plan <ArrowUpRight className="h-3 w-3" />
          </a>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PlanStat
            icon={Sparkles}
            label="Current plan"
            value={planName}
            accent
            sub={billingInterval === 'yearly' ? 'Yearly billing' : 'Monthly billing'}
          />
          <PlanStat
            icon={Zap}
            label="Credit balance"
            value={balance.toLocaleString()}
            sub={`${refillPct}% of monthly refill`}
            tone={refillPct > 80 ? 'good' : refillPct < 20 ? 'bad' : 'neutral'}
          />
          <PlanStat
            icon={Activity}
            label="Monthly refill"
            value={refill ? refill.toLocaleString() : '—'}
            sub="Refreshes on the 1st"
          />
          <PlanStat
            icon={TrendingUp}
            label="Concurrency"
            value={concurrency.toLocaleString()}
            sub="Max simultaneous requests"
          />
        </div>
      </Card.Body>
    </Card>
  );
}

function PlanStat({ icon: Icon, label, value, sub, accent, tone }) {
  const toneClasses = {
    good: 'text-success',
    bad: 'text-destructive',
    neutral: 'text-fg',
  }[tone || 'neutral'];
  return (
    <div className={`relative rounded-xl p-3 border transition-all ${
      accent
        ? 'bg-gradient-to-br from-brand-softest to-surface border-brand-soft'
        : 'bg-surface-2/50 border-border'
    }`}>
      <div className="flex items-center justify-between">
        <div className="text-2xs uppercase tracking-wider text-fg-subtle font-medium">{label}</div>
        <Icon className={`h-3.5 w-3.5 ${accent ? 'text-brand-strong' : 'text-fg-subtle'}`} />
      </div>
      <div className={`mt-1.5 text-lg font-semibold ${accent ? 'text-brand-strong' : toneClasses}`}>{value}</div>
      {sub && <div className="mt-0.5 text-2xs text-fg-muted">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------
   ACTIVITY CARD — usage ribbon + cute footprint
   ------------------------------------------------------------------ */
function ActivityCard({ usageThisMonth, apiKeyCount, memberSince }) {
  const requests = usageThisMonth?.total_requests ?? null;
  const domains = usageThisMonth?.unique_domains ?? null;

  // Daily activity sparkline — show last 7 days, with weekend dimmer
  const daily = usageThisMonth?.daily || [];
  const max = Math.max(1, ...daily.map((d) => d.count || 0));

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-brand-softest border border-brand-soft flex items-center justify-center">
            <Activity className="h-4 w-4 text-brand-strong" />
          </div>
          <div>
            <Card.Title>Activity</Card.Title>
            <Card.Subtitle>A little footprint of your time here.</Card.Subtitle>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        {/* 30-day ribbon */}
        {requests !== null && (
          <div className="rounded-xl bg-surface-2/50 border border-border p-4">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-2xs uppercase tracking-wider text-fg-subtle">Last 30 days</div>
                <div className="text-2xl font-semibold text-fg mt-0.5">
                  {requests.toLocaleString()} <span className="text-sm font-normal text-fg-muted">requests</span>
                </div>
              </div>
              {domains !== null && (
                <div className="text-right">
                  <div className="text-2xs uppercase tracking-wider text-fg-subtle">Across</div>
                  <div className="text-lg font-semibold text-fg mt-0.5">{domains} domains</div>
                </div>
              )}
            </div>
            {/* 7-day mini bar chart */}
            {daily.length > 0 && (
              <div className="mt-3">
                <div className="flex items-end gap-1 h-12">
                  {daily.slice(-7).map((d, i) => {
                    const h = d.count > 0 ? Math.max(8, Math.round((d.count / max) * 48)) : 3;
                    const isToday = i === daily.slice(-7).length - 1;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date || ''}: ${d.count || 0} requests`}>
                        <div
                          className={`w-full rounded-t-sm transition-all ${
                            isToday
                              ? 'bg-gradient-to-t from-brand to-orange-300'
                              : d.count > 0
                              ? 'bg-brand-soft hover:bg-brand'
                              : 'bg-surface-3'
                          }`}
                          style={{ height: `${h}px` }}
                        />
                        <div className="text-2xs text-fg-subtle font-mono">
                          {(d.date || '').slice(-2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-2xs text-fg-subtle">
                  <span className="inline-block h-2 w-2 rounded-sm bg-brand" />
                  Last 7 days ·{' '}
                  <span className="inline-block h-2 w-2 rounded-sm bg-gradient-to-t from-brand to-orange-300" />
                  Today
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mini stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MiniStat
            icon={KeyRound}
            label="API keys"
            value={apiKeyCount}
            sub={apiKeyCount === 1 ? '1 active' : `${apiKeyCount} active`}
          />
          <MiniStat
            icon={Award}
            label="Loyalty"
            value={memberSince ? `${Math.max(1, Math.floor((Date.now() - new Date(memberSince).getTime()) / 86400000))}d` : '—'}
            sub="Member for"
          />
          <MiniStat
            icon={Star}
            label="Status"
            value="Active"
            sub="All systems green"
            tone="good"
          />
        </div>
      </Card.Body>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value, sub, tone }) {
  const toneClass = tone === 'good' ? 'text-success' : 'text-fg';
  return (
    <div className="rounded-xl bg-surface-2/50 border border-border p-3">
      <div className="flex items-center justify-between">
        <div className="text-2xs uppercase tracking-wider text-fg-subtle font-medium">{label}</div>
        <Icon className="h-3.5 w-3.5 text-fg-subtle" />
      </div>
      <div className={`mt-1.5 text-base font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-2xs text-fg-muted">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------
   SHARED TILES
   ------------------------------------------------------------------ */
function InfoTile({ icon: Icon, label, value, mono, copyable }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-xl bg-surface-2/50 border border-border p-3 group">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle font-medium flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <div className={`text-sm text-fg truncate flex-1 min-w-0 ${mono ? 'font-mono text-2xs' : ''}`}>
          {value}
        </div>
        {copyable && (
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch { /* ignore */ }
            }}
            className="text-2xs text-fg-subtle hover:text-brand opacity-0 group-hover:opacity-100 transition"
            aria-label="Copy"
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : 'Copy'}
          </button>
        )}
      </div>
    </div>
  );
}